import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import { useRef } from "react";
import { useRecoilCallback } from "recoil";
import {
  DataState,
  onboardingPromotionDataState,
} from "../recoil/onboardingPromotionDataAtom";
import { useOnboardingPromotionData } from "./useOnboardingPromotionData";
import { webFunnelApi } from "../api/webFunnelApi";
import { captureException } from "../utils/sentry";

interface UseOnboardingPageProps {
  pageKey: string;
  nextPageRoute?: string;
}

export function useOnboardingPromotionPage({
  pageKey,
  nextPageRoute,
}: UseOnboardingPageProps) {
  const navigate = useNavigate();
  const pageDataHook = useOnboardingPromotionData(pageKey);
  const isSavingRef = useRef(false);

  const { mutate: savePageData } = useMutation(
    ({ distinctId, dataToSave }: { distinctId: string; dataToSave: DataState }) =>
      webFunnelApi.savePageData(distinctId, pageKey, dataToSave),
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error(`[${pageKey}] 프로모션 온보딩 데이터 저장 최종 실패:`, error);
        captureException(error, {
          tags: { action: "web_funnel_save" },
          extra: { pageKey },
        });
      },
    }
  );

  const handleNext = useRecoilCallback(
    ({ snapshot }) =>
      async (dataToSave?: DataState) => {
        if (isSavingRef.current) return;

        let currentPageData: DataState;
        if (dataToSave) {
          currentPageData = dataToSave;
        } else {
          const latestOnboardingData =
            await snapshot.getPromise(onboardingPromotionDataState);
          currentPageData = latestOnboardingData[pageKey] ?? {};
        }

        const path =
          typeof window !== "undefined" ? window.location.pathname : "";
        const instanceName = path.includes("/v6-")
          ? "promotion"
          : path.includes("/v5-")
            ? "v5"
            : "promotion";
        const instance =
          (mixpanel as any)[instanceName] ?? (mixpanel as any);
        const distinctIdFromMixpanel = instance?.get_distinct_id?.();
        const distinctId =
          distinctIdFromMixpanel ??
          localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ??
          undefined;

        if (distinctId) {
          isSavingRef.current = true;
          savePageData(
            { distinctId, dataToSave: currentPageData },
            {
              onSettled: () => {
                isSavingRef.current = false;
              },
            }
          );
        } else {
          console.warn(
            `[${pageKey}] mixpanel distinctId를 찾지 못해 webFunnel 저장을 건너뜁니다.`
          );
        }

        if (nextPageRoute) {
          navigate(nextPageRoute, {
            replace: true,
          });
        }
      },
    [pageKey, nextPageRoute, navigate, savePageData]
  );

  return {
    ...pageDataHook,
    handleNext,
  };
}
